/**
 * @file Thin data hooks binding services to the async state hook. Components
 * consume these instead of touching services directly.
 */

import { useAsync } from './useAsync';
import { CompetitionService } from '../services/CompetitionService';
import { ArchiveService } from '../services/ArchiveService';
import { LeaderboardService } from '../services/LeaderboardService';

/**
 * @typedef {import('../models/manifest').Competition} Competition
 * @typedef {import('../models/archive').ArchiveIndexEntry} ArchiveIndexEntry
 * @typedef {import('../models/leaderboard').Leaderboard} Leaderboard
 */

/** @returns {import('./useAsync').AsyncState<Competition>} */
export function useCurrentCompetition() {
  return useAsync(() => CompetitionService.getCurrentCompetition(), []);
}

/**
 * Load any competition by its manifest URL (current or archived — same path).
 * @param {string} manifestUrl
 * @returns {import('./useAsync').AsyncState<Competition>}
 */
export function useManifestByUrl(manifestUrl) {
  return useAsync(
    () => CompetitionService.getCompetitionByManifestUrl(manifestUrl),
    [manifestUrl]
  );
}

/**
 * The archive index (list of past competitions). Small; drives the grid.
 * @returns {import('./useAsync').AsyncState<ArchiveIndexEntry[]>}
 */
export function useArchiveIndex() {
  return useAsync(() => ArchiveService.getArchiveIndex(), []);
}

/**
 * Lazily load one archive entry's full (cached) manifest — used per card.
 * @param {ArchiveIndexEntry} entry
 * @returns {import('./useAsync').AsyncState<Competition>}
 */
export function useEntryManifest(entry) {
  const key = entry ? entry.id || entry.manifestUrl : 'none';
  return useAsync(() => ArchiveService.getCompetitionForEntry(entry), [key]);
}

/**
 * Resolve a full archived competition by id (index lookup + manifest).
 * @param {string} id
 * @returns {import('./useAsync').AsyncState<{ entry: ArchiveIndexEntry, manifestUrl: string, competition: Competition }>}
 */
export function useArchivedById(id) {
  return useAsync(() => ArchiveService.getById(id), [id]);
}

/**
 * @param {number} season
 * @param {number} week
 * @returns {import('./useAsync').AsyncState<Leaderboard>}
 */
export function useLeaderboard(season, week) {
  return useAsync(
    () => LeaderboardService.getLeaderboard(season, week),
    [season, week]
  );
}
